import { useRef, useState } from "react";
import { Button, Spin } from "antd";
import { Mic, MicOff, Share2, Sparkles, RotateCcw } from "lucide-react";
import { apiRequest } from "@vojomo-core/shared";

// Web Speech API types (not in all TS lib versions)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  start(): void;
  stop(): void;
}
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition: new () => SpeechRecognitionInstance;
  }
}

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

// ─── Types ───────────────────────────────────────────────────────────────────

interface GroupSummary {
  narrative: string;
  q1_accomplish: string;
  q2_happened: string;
  q3_why: string;
  q4_start: string;
}

interface AARSession {
  id: string;
  summary_shared: boolean;
  shared_with_users: string[];
  [key: string]: unknown;
}

interface AARGroupSessionProps {
  sessionId: string;
  isClosed?: boolean;
  onSummaryShared?: (session: AARSession) => void;
}

const QUESTIONS = [
  { key: "q1_accomplish", label: "What was expected to happen?" },
  { key: "q2_happened",   label: "What actually occurred?" },
  { key: "q3_why",        label: "What went well and why? What should we commit to continuing?" },
  { key: "q4_start",      label: "What can be improved and how?" },
];

const BRAND = "rgb(44, 21, 97)";

// ─── Recording hook ───────────────────────────────────────────────────────────

function useSimpleRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [liveText, setLiveText] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(200);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      setLiveText("");

      // Optional live transcript via Web Speech API (best-effort)
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.onresult = (e) => {
          const transcript = Array.from(e.results).map((r) => r[0].transcript).join(" ");
          setLiveText(transcript);
        };
        rec.start();
        recognitionRef.current = rec;
      }
    } catch {
      setIsRecording(false);
    }
  };

  const stop = async (sessionId: string): Promise<string> => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;

    return new Promise((resolve) => {
      const mr = mediaRecorderRef.current;
      if (!mr) { resolve(""); return; }

      mr.onstop = async () => {
        setIsProcessing(true);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("file", blob, "recording.webm");

        const transcript = await apiRequest<string>({
          subRoute: "audio",
          path: `/transcribe_file?ref_type=aar&ref_id=${sessionId}`,
          method: "POST",
          formData,
        });

        setIsProcessing(false);
        setIsRecording(false);
        setLiveText("");
        resolve(transcript ?? "");
      };

      mr.stop();
      mr.stream.getTracks().forEach((t) => t.stop());
    });
  };

  return { isRecording, isProcessing, liveText, start, stop };
}

// ─── SSE synthesis helper ─────────────────────────────────────────────────────

async function streamSynthesize(
  sessionId: string,
  body: { transcript?: string; section_transcripts?: Record<string, string> },
  onChunk: (text: string) => void
): Promise<string> {
  const resp = await fetch(
    `${SERVER_URL}/aar/sessions/${sessionId}/group-synthesize`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    }
  );

  const reader = resp.body?.getReader();
  const decoder = new TextDecoder();
  let fullText = "";

  if (reader) {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const lines = decoder.decode(value).split("\n");
      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === "chunk") {
              fullText += event.content;
              onChunk(fullText);
            }
          } catch { /* ignore */ }
        }
      }
    }
  }

  return fullText;
}

function parseSynthesisResult(fullText: string): {
  narrative: string;
  structured: Partial<GroupSummary>;
} {
  const jsonMatch = fullText.match(/```json\s*([\s\S]*?)```/);
  let structured: Partial<GroupSummary> = {};
  if (jsonMatch) {
    try { structured = JSON.parse(jsonMatch[1]); } catch { /* ignore */ }
  }
  const narrative = jsonMatch
    ? fullText.slice(0, fullText.indexOf("```json")).trim()
    : fullText.trim();
  return { narrative, structured };
}

// ─── Open Discussion mode ─────────────────────────────────────────────────────

function OpenDiscussionMode({
  sessionId,
  onSummaryReady,
  isClosed,
}: {
  sessionId: string;
  onSummaryReady: (summary: GroupSummary, transcript: string) => void;
  isClosed: boolean;
}) {
  const recorder = useSimpleRecorder();
  const [fullTranscript, setFullTranscript] = useState("");
  const [synthesizing, setSynthesizing] = useState(false);
  const [streamText, setStreamText] = useState("");

  const handleStop = async () => {
    const transcript = await recorder.stop(sessionId);
    setFullTranscript(transcript);
  };

  const handleSynthesize = async () => {
    if (!fullTranscript.trim()) return;
    setSynthesizing(true);
    setStreamText("");

    const fullText = await streamSynthesize(
      sessionId,
      { transcript: fullTranscript },
      setStreamText
    );

    setSynthesizing(false);

    const { narrative, structured } = parseSynthesisResult(fullText);
    onSummaryReady(
      {
        narrative,
        q1_accomplish: structured.q1_accomplish ?? "",
        q2_happened: structured.q2_happened ?? "",
        q3_why: structured.q3_why ?? "",
        q4_start: structured.q4_start ?? "",
      },
      fullTranscript
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Hit record, let the whole team discuss freely, then stop and Vo will synthesize.
      </p>

      <div className="flex items-center gap-3">
        {!recorder.isRecording ? (
          <Button
            type="primary"
            icon={<Mic size={16} />}
            onClick={recorder.start}
            disabled={isClosed || recorder.isProcessing}
            style={{ backgroundColor: BRAND }}
          >
            Start Recording
          </Button>
        ) : (
          <Button
            danger
            icon={<MicOff size={16} />}
            onClick={handleStop}
          >
            Stop Recording
          </Button>
        )}
        {recorder.isProcessing && <Spin size="small" />}
      </div>

      {recorder.isRecording && recorder.liveText && (
        <div className="text-sm text-gray-400 italic border rounded p-3 bg-gray-50 min-h-12">
          {recorder.liveText}
        </div>
      )}

      {fullTranscript && !recorder.isRecording && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500">
            Transcript captured ({fullTranscript.split(" ").length} words)
          </p>
          <Button
            type="primary"
            icon={<Sparkles size={15} />}
            onClick={handleSynthesize}
            loading={synthesizing}
            style={{ backgroundColor: BRAND }}
          >
            Synthesize with Vo
          </Button>
          {synthesizing && streamText && (
            <div className="text-sm text-gray-700 border rounded p-3 bg-purple-50 whitespace-pre-wrap">
              {streamText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Guided by Vo mode ─────────────────────────────────────────────────────────

function GuidedMode({
  sessionId,
  onSummaryReady,
  isClosed,
}: {
  sessionId: string;
  onSummaryReady: (summary: GroupSummary, transcript: string) => void;
  isClosed: boolean;
}) {
  const recorder = useSimpleRecorder();
  const [currentStep, setCurrentStep] = useState(0);
  const [sectionTranscripts, setSectionTranscripts] = useState<Record<string, string>>({});
  const [synthesizing, setSynthesizing] = useState(false);

  const handleStopSection = async () => {
    const transcript = await recorder.stop(sessionId);
    const key = QUESTIONS[currentStep].key;
    setSectionTranscripts((prev) => ({ ...prev, [key]: transcript }));
  };

  const handleSynthesize = async () => {
    setSynthesizing(true);

    const fullText = await streamSynthesize(
      sessionId,
      { section_transcripts: sectionTranscripts },
      () => {}
    );

    setSynthesizing(false);

    const { narrative, structured } = parseSynthesisResult(fullText);
    onSummaryReady(
      {
        narrative,
        q1_accomplish: structured.q1_accomplish ?? "",
        q2_happened: structured.q2_happened ?? "",
        q3_why: structured.q3_why ?? "",
        q4_start: structured.q4_start ?? "",
      },
      Object.values(sectionTranscripts).join("\n\n")
    );
  };

  const allSectionsRecorded = QUESTIONS.every((q) => sectionTranscripts[q.key] !== undefined);
  const currentQ = QUESTIONS[currentStep];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Vo guides the team through each question one at a time.
      </p>

      {/* Progress indicators */}
      <div className="flex gap-1.5 flex-wrap">
        {QUESTIONS.map((q, i) => (
          <button
            key={q.key}
            onClick={() => {
              if (sectionTranscripts[q.key] !== undefined || i <= currentStep) {
                setCurrentStep(i);
              }
            }}
            className={`text-xs px-2 py-1 rounded-full border transition-colors ${
              sectionTranscripts[q.key] !== undefined
                ? "bg-purple-100 border-purple-300 text-purple-800"
                : i === currentStep
                ? "bg-white border-purple-500 text-purple-700 font-medium"
                : "bg-gray-50 border-gray-200 text-gray-400"
            }`}
          >
            Q{i + 1}
          </button>
        ))}
      </div>

      {/* Current question */}
      <div className="border rounded-lg p-4 bg-purple-50 space-y-3">
        <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">
          Question {currentStep + 1} of {QUESTIONS.length}
        </p>
        <p className="font-semibold text-sm text-gray-900">{currentQ.label}</p>

        {sectionTranscripts[currentQ.key] !== undefined ? (
          <div className="space-y-2">
            <p className="text-xs text-green-700 font-medium">✓ Recorded</p>
            <p className="text-sm text-gray-700 italic">
              {sectionTranscripts[currentQ.key] || "(no transcript)"}
            </p>
            <button
              className="text-xs text-purple-600 underline"
              onClick={() => {
                setSectionTranscripts((prev) => {
                  const n = { ...prev };
                  delete n[currentQ.key];
                  return n;
                });
              }}
            >
              Re-record
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            {!recorder.isRecording ? (
              <Button
                type="primary"
                icon={<Mic size={15} />}
                onClick={recorder.start}
                disabled={isClosed || recorder.isProcessing}
                style={{ backgroundColor: BRAND }}
                size="small"
              >
                Record discussion
              </Button>
            ) : (
              <Button
                danger
                icon={<MicOff size={15} />}
                onClick={handleStopSection}
                size="small"
              >
                Stop
              </Button>
            )}
            {recorder.isProcessing && <Spin size="small" />}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button
          size="small"
          onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
          disabled={currentStep === 0}
        >
          ← Previous
        </Button>
        {currentStep < QUESTIONS.length - 1 ? (
          <Button
            size="small"
            type="primary"
            onClick={() => setCurrentStep((s) => s + 1)}
            disabled={!sectionTranscripts[currentQ.key]}
            style={{ backgroundColor: BRAND }}
          >
            Next →
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<Sparkles size={14} />}
            onClick={handleSynthesize}
            loading={synthesizing}
            disabled={!allSectionsRecorded}
            style={{ backgroundColor: BRAND }}
          >
            Synthesize with Vo
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Summary display ──────────────────────────────────────────────────────────

function GroupSummaryView({
  summary,
  sessionId,
  onRedo,
  onSummaryShared,
}: {
  summary: GroupSummary;
  sessionId: string;
  onRedo: () => void;
  onSummaryShared?: (session: AARSession) => void;
}) {
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  const handleShare = async () => {
    setSharing(true);
    const data = await apiRequest<{ shared_with: number; session: AARSession }>({
      subRoute: "aar",
      path: `/sessions/${sessionId}/share`,
      method: "POST",
    });
    setSharing(false);
    setShared(true);
    if (data?.session && onSummaryShared) onSummaryShared(data.session);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-purple-600" />
          <span className="text-sm font-semibold text-purple-900">
            Vo's Team Summary
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!shared ? (
            <Button
              size="small"
              icon={<Share2 size={13} />}
              onClick={handleShare}
              loading={sharing}
              style={{ borderColor: "rgb(44, 21, 97)", color: "rgb(44, 21, 97)" }}
            >
              Share with Team
            </Button>
          ) : (
            <span className="text-xs text-green-600 font-medium">✓ Shared with team</span>
          )}
          <button
            onClick={onRedo}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            <RotateCcw size={12} /> Re-record
          </button>
        </div>
      </div>

      {summary.narrative && (
        <div className="border rounded-lg p-4 bg-purple-50 space-y-1">
          <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">
            Narrative
          </p>
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {summary.narrative}
          </p>
        </div>
      )}

      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Structured Summary
        </p>
        {QUESTIONS.map((q) => {
          const answer = summary[q.key as keyof GroupSummary];
          if (!answer) return null;
          return (
            <div key={q.key} className="border rounded-lg p-3 bg-white">
              <p className="text-xs font-medium text-gray-500 mb-1">{q.label}</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{answer}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AARGroupSession({ sessionId, isClosed = false, onSummaryShared }: AARGroupSessionProps) {
  const [recordingMode, setRecordingMode] = useState<"open" | "guided">("open");
  const [summary, setSummary] = useState<GroupSummary | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSummaryReady = async (newSummary: GroupSummary, transcript: string) => {
    setSummary(newSummary);
    setSaving(true);
    await apiRequest({
      subRoute: "aar",
      path: `/sessions/${sessionId}/group-summary`,
      method: "POST",
      body: { ...newSummary, transcript },
    });
    setSaving(false);
    setSaved(true);
  };

  if (summary) {
    return (
      <div className="space-y-3">
        {saving && <p className="text-xs text-gray-400">Saving…</p>}
        {saved && <p className="text-xs text-green-600">✓ Saved</p>}
        <GroupSummaryView
          summary={summary}
          sessionId={sessionId}
          onRedo={() => { setSummary(null); setSaved(false); }}
          onSummaryShared={onSummaryShared}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Mode selector */}
      {!isClosed && (
        <div className="flex gap-2">
          <button
            onClick={() => setRecordingMode("open")}
            className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
              recordingMode === "open"
                ? "bg-purple-50 border-purple-400 text-purple-900"
                : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            Open Discussion
          </button>
          <button
            onClick={() => setRecordingMode("guided")}
            className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
              recordingMode === "guided"
                ? "bg-purple-50 border-purple-400 text-purple-900"
                : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            Guided by Vo
          </button>
        </div>
      )}

      {isClosed ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          This session is closed. No new recordings.
        </div>
      ) : recordingMode === "open" ? (
        <OpenDiscussionMode
          sessionId={sessionId}
          onSummaryReady={handleSummaryReady}
          isClosed={isClosed}
        />
      ) : (
        <GuidedMode
          sessionId={sessionId}
          onSummaryReady={handleSummaryReady}
          isClosed={isClosed}
        />
      )}
    </div>
  );
}
